<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <h1 class="dashboard-title">Panel de Analista</h1>
            <p class="text-caption grey--text">
              Resumen general de operaciones &bull; {{ currentDate }}
            </p>
          </div>
          <div class="d-flex align-center ga-2">
            <v-text-field
              v-model="dateFrom"
              label="Desde"
              outlined
              dense
              type="date"
              hide-details
              class="date-input"
            ></v-text-field>
            <v-text-field
              v-model="dateTo"
              label="Hasta"
              outlined
              dense
              type="date"
              hide-details
              class="date-input"
            ></v-text-field>
            <v-btn color="primary" @click="loadAllData" :loading="loading" depressed>
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- KPI Cards -->
    <v-row class="mb-4">
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex success-gradient">
              <v-icon color="white">mdi-cash-register</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Ventas Periodo</div>
              <div class="kpi-value">{{ formatCurrency(kpis.totalSales) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                {{ kpis.totalTransactions }} transacciones
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex primary-gradient">
              <v-icon color="white">mdi-package-variant-closed</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Valor Inventario</div>
              <div class="kpi-value">{{ formatCurrency(kpis.inventoryValue) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                {{ kpis.totalProducts }} productos
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex warning-gradient">
              <v-icon color="white">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Sin Rotaci&oacute;n</div>
              <div class="kpi-value">{{ kpis.deadStock }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                productos sin movimiento
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex info-gradient">
              <v-icon color="white">mdi-percent</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Margen Promedio</div>
              <div class="kpi-value">{{ kpis.avgMargin }}%</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                Utilidad: {{ formatCurrency(kpis.grossProfit) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Navigation Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3" v-for="nav in navCards" :key="nav.route">
        <v-card
          class="nav-card elevation-0"
          outlined
          hover
          @click="$router.push({ name: nav.route })"
        >
          <v-card-text class="d-flex align-center pa-4">
            <v-avatar :color="nav.color" size="44" class="mr-3">
              <v-icon color="white">{{ nav.icon }}</v-icon>
            </v-avatar>
            <div>
              <div class="nav-card-title">{{ nav.title }}</div>
              <div class="text-caption grey--text">{{ nav.description }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tables Section -->
    <v-row>
      <!-- Top Products -->
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="success">mdi-trending-up</v-icon>
            Top Productos (por ventas)
          </v-card-title>
          <v-data-table
            :headers="topProductHeaders"
            :items="topProducts"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="10"
            class="analyst-table"
            no-data-text="Sin datos para el periodo"
          >
            <template v-slot:item.total="{ item }">
              {{ formatCurrency(item.total) }}
            </template>
            <template v-slot:item.margin="{ item }">
              <v-chip
                :color="item.margin > 20 ? 'success' : item.margin > 10 ? 'warning' : 'error'"
                text-color="white"
                x-small
              >
                {{ item.margin }}%
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Low Stock Alerts -->
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="warning">mdi-alert-outline</v-icon>
            Alertas de Inventario
          </v-card-title>
          <v-data-table
            :headers="alertHeaders"
            :items="inventoryAlerts"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="10"
            class="analyst-table"
            no-data-text="Sin alertas"
          >
            <template v-slot:item.stock="{ item }">
              <v-chip
                :color="item.stock === 0 ? 'error' : 'warning'"
                text-color="white"
                x-small
              >
                {{ item.stock }}
              </v-chip>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip outlined x-small>{{ item.type }}</v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";

export default {
  name: "AnalystDashboard",

  data() {
    return {
      loading: false,
      dateFrom: new Date(new Date().setDate(new Date().getDate() - 30))
        .toISOString()
        .substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),

      kpis: {
        totalSales: 0,
        totalTransactions: 0,
        inventoryValue: 0,
        totalProducts: 0,
        deadStock: 0,
        avgMargin: 0,
        grossProfit: 0,
      },

      navCards: [
        {
          title: "Inventario",
          description: "Stock actual y valorizado",
          icon: "mdi-package-variant-closed",
          color: "success",
          route: "Inventory",
        },
        {
          title: "Reporte de Caja",
          description: "Ventas y formas de pago",
          icon: "mdi-cash-register",
          color: "primary",
          route: "Cash_report",
        },
        {
          title: "Resumen Diario",
          description: "Operaciones del dia",
          icon: "mdi-chart-line",
          color: "info",
          route: "Resume",
        },
        {
          title: "Buscar Productos",
          description: "Buscar en todas las tiendas",
          icon: "mdi-cloud-search-outline",
          color: "purple",
          route: "G_search",
        },
      ],

      topProductHeaders: [
        { text: "Producto", value: "name", sortable: false },
        { text: "Cant.", value: "qty", align: "end" },
        { text: "Total", value: "total", align: "end" },
        { text: "Margen", value: "margin", align: "center" },
      ],
      topProducts: [],

      alertHeaders: [
        { text: "Producto", value: "name", sortable: false },
        { text: "Stock", value: "stock", align: "center" },
        { text: "Tipo", value: "type", align: "center" },
      ],
      inventoryAlerts: [],
    };
  },

  computed: {
    currentDate() {
      return new Date().toLocaleDateString("es-CO", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },

  mounted() {
    this.loadAllData();
  },

  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    loadAllData() {
      this.loading = true;
      const store = this.$store.state.profile.store_id || window.store?.store_id;

      webserver(
        "get_analyst_dashboard",
        {
          store: store,
          date_from: this.dateFrom,
          date_to: this.dateTo,
        },
        (data) => {
          this.loading = false;
          if (data && !data.error) {
            this.kpis = {
              totalSales: data.total_sales || 0,
              totalTransactions: data.total_transactions || 0,
              inventoryValue: data.inventory_value || 0,
              totalProducts: data.total_products || 0,
              deadStock: data.dead_stock || 0,
              avgMargin: data.avg_margin || 0,
              grossProfit: data.gross_profit || 0,
            };
            this.topProducts = data.top_products || [];
            this.inventoryAlerts = data.inventory_alerts || [];
          }
        }
      );
    },
  },
};
</script>

<style scoped>
.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #424242;
}

.date-input {
  max-width: 160px;
}

/* KPI Cards */
.kpi-card {
  border-radius: 8px;
  transition: box-shadow 0.2s ease;
}

.kpi-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.success-gradient {
  background: linear-gradient(135deg, #1e8e3e, #34a853);
}

.primary-gradient {
  background: linear-gradient(135deg, #1a73e8, #4285f4);
}

.warning-gradient {
  background: linear-gradient(135deg, #f9ab00, #fbbc04);
}

.info-gradient {
  background: linear-gradient(135deg, #1967d2, #4285f4);
}

.kpi-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #5f6368;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #202124;
  line-height: 1.3;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #80868b;
  margin-top: 2px;
}

/* Nav Cards */
.nav-card {
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-card:hover {
  border-color: #1a73e8 !important;
  box-shadow: 0 2px 8px rgba(26, 115, 232, 0.12) !important;
}

.nav-card-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #202124;
}

/* Section */
.section-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #424242;
}

.analyst-table {
  font-size: 0.8125rem;
}

/* Responsive */
@media (max-width: 960px) {
  .date-input {
    max-width: 130px;
  }
}

@media (max-width: 600px) {
  .kpi-value {
    font-size: 1rem;
  }

  .date-input {
    max-width: 110px;
  }
}
</style>
